import React from 'react';
import { InlineMicButton } from '../InlineMicButton';
import type {
  BabySex, DeliveryMode, Hospital, UterotonicOption,
} from '../../types/clinical';

/**
 * SASOG Patient Delivery Report form — parallel to SurgeryForm but dedicated to obstetric
 * vaginal delivery records. Narrative textareas (history, notes, post partum) use the same
 * InlineMicButton "append" pattern as the surgery form — hence the setters are typed as
 * React.Dispatch<React.SetStateAction<string>> so transcribed text appends without clobbering.
 */
export interface VaginalDeliveryFormProps {
  vdDate: string;
  setVdDate: (v: string) => void;
  vdTime: string;
  setVdTime: (v: string) => void;
  vdHospital: string;
  setVdHospital: (v: string) => void;
  vdDoctorName: string;
  setVdDoctorName: (v: string) => void;
  vdDoctorEmail: string;
  setVdDoctorEmail: (v: string) => void;
  vdPaediatrician: string;
  setVdPaediatrician: (v: string) => void;
  vdWardSister: string;
  setVdWardSister: (v: string) => void;
  vdUmbilicalCordPh: string;
  setVdUmbilicalCordPh: (v: string) => void;
  vdDeliveryMode: DeliveryMode;
  setVdDeliveryMode: (v: DeliveryMode) => void;
  vdUterotonic: UterotonicOption;
  setVdUterotonic: (v: UterotonicOption) => void;
  vdUterotonicOther: string;
  setVdUterotonicOther: (v: string) => void;
  vdHistory: string;
  setVdHistory: React.Dispatch<React.SetStateAction<string>>;
  vdNotes: string;
  setVdNotes: React.Dispatch<React.SetStateAction<string>>;
  vdPostPartum: string;
  setVdPostPartum: React.Dispatch<React.SetStateAction<string>>;
  babyWeight: number | null;
  setVdBabyWeight: (v: number | null) => void;
  babySex: BabySex;
  setVdBabySex: (v: BabySex) => void;
  apgar1min: number | null;
  setVdApgar1: (v: number | null) => void;
  apgar5min: number | null;
  setVdApgar5: (v: number | null) => void;
  bcgGiven: boolean;
  setVdBcgGiven: (v: boolean) => void;
  polioGiven: boolean;
  setVdPolioGiven: (v: boolean) => void;
  hospitals: Hospital[];
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function VaginalDeliveryForm(props: VaginalDeliveryFormProps) {
  const {
    vdDate, setVdDate, vdTime, setVdTime, vdHospital, setVdHospital,
    vdDoctorName, setVdDoctorName, vdDoctorEmail, setVdDoctorEmail,
    vdPaediatrician, setVdPaediatrician, vdWardSister, setVdWardSister,
    vdUmbilicalCordPh, setVdUmbilicalCordPh,
    vdDeliveryMode, setVdDeliveryMode,
    vdUterotonic, setVdUterotonic, vdUterotonicOther, setVdUterotonicOther,
    vdHistory, setVdHistory, vdNotes, setVdNotes, vdPostPartum, setVdPostPartum,
    babyWeight, setVdBabyWeight, babySex, setVdBabySex,
    apgar1min, setVdApgar1, apgar5min, setVdApgar5,
    bcgGiven, setVdBcgGiven, polioGiven, setVdPolioGiven,
    hospitals, saving, onSave, onCancel,
  } = props;

  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Row 1: Date, Time */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Date *</label>
          <input
            type="date"
            value={vdDate}
            onChange={e => setVdDate(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Time</label>
          <input
            type="time"
            value={vdTime}
            onChange={e => setVdTime(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Row 2: Hospital, Doctor Name */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Hospital</label>
          <select
            value={vdHospital}
            onChange={e => setVdHospital(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select —</option>
            {hospitals.map(h => <option key={h.id} value={h.name}>{h.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Doctor Name</label>
          <input
            value={vdDoctorName}
            onChange={e => setVdDoctorName(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Row 3: Doctor Email, Paediatrician */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Doctor Email</label>
          <input
            type="email"
            value={vdDoctorEmail}
            onChange={e => setVdDoctorEmail(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Paediatrician</label>
          <input
            value={vdPaediatrician}
            onChange={e => setVdPaediatrician(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Row 4: Ward Sister */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Ward Sister</label>
        <input
          value={vdWardSister}
          onChange={e => setVdWardSister(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* Row 5: Umbilical Cord pH */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Umbilical Cord pH</label>
        <input
          value={vdUmbilicalCordPh}
          onChange={e => setVdUmbilicalCordPh(e.target.value)}
          placeholder="e.g. 7.25"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* History (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">History</label>
          <InlineMicButton onTranscript={t => setVdHistory(prev => (prev ? prev + ' ' + t : t))} />
        </div>
        <textarea
          value={vdHistory}
          onChange={e => setVdHistory(e.target.value)}
          rows={6}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* Row 6: Delivery Mode */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Delivery Mode</label>
        <select
          value={vdDeliveryMode}
          onChange={e => setVdDeliveryMode(e.target.value as DeliveryMode)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
        >
          <option value="">— Select —</option>
          <option value="NVD">Normal Vaginal Delivery (NVD)</option>
          <option value="Vacuum">Vacuum (Ventouse)</option>
          <option value="Forceps">Forceps</option>
          <option value="Breech">Vaginal Breech Delivery</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Row 7: Uterotonic */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Uterotonic</label>
        <select
          value={vdUterotonic}
          onChange={e => {
            const v = e.target.value as UterotonicOption;
            setVdUterotonic(v);
            if (v !== 'Other') setVdUterotonicOther('');
          }}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
        >
          <option value="">— Select —</option>
          <option value="Syntocinon">Syntocinon</option>
          <option value="Syntometrine">Syntometrine</option>
          <option value="Other">Other</option>
        </select>
        {vdUterotonic === 'Other' && (
          <input
            value={vdUterotonicOther}
            onChange={e => setVdUterotonicOther(e.target.value)}
            placeholder="Specify uterotonic"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded mt-1"
          />
        )}
      </div>

      {/* Baby Details */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Baby Details</label>
        <div className="grid grid-cols-4 gap-3 mb-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Weight (g)</label>
            <input
              type="number"
              step={10}
              min={0}
              max={6000}
              value={babyWeight ?? ''}
              onChange={e => setVdBabyWeight(e.target.value ? parseInt(e.target.value) : null)}
              placeholder="e.g. 3200"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sex</label>
            <select
              value={babySex}
              onChange={e => setVdBabySex(e.target.value as BabySex)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
            >
              <option value="">— Select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Apgar 1 min</label>
            <input
              type="number"
              min={0}
              max={10}
              value={apgar1min ?? ''}
              onChange={e => setVdApgar1(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Apgar 5 min</label>
            <input
              type="number"
              min={0}
              max={10}
              value={apgar5min ?? ''}
              onChange={e => setVdApgar5(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            />
          </div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={bcgGiven}
              onChange={e => setVdBcgGiven(e.target.checked)}
              className="h-4 w-4 rounded"
            />
            BCG Given
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={polioGiven}
              onChange={e => setVdPolioGiven(e.target.checked)}
              className="h-4 w-4 rounded"
            />
            Polio Given
          </label>
        </div>
      </div>

      {/* Notes (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Notes</label>
          <InlineMicButton onTranscript={t => setVdNotes(prev => (prev ? prev + ' ' + t : t))} />
        </div>
        <textarea
          value={vdNotes}
          onChange={e => setVdNotes(e.target.value)}
          rows={6}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* Post Partum (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Post Partum</label>
          <InlineMicButton onTranscript={t => setVdPostPartum(prev => (prev ? prev + ' ' + t : t))} />
        </div>
        <textarea
          value={vdPostPartum}
          onChange={e => setVdPostPartum(e.target.value)}
          rows={6}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={onSave}
          disabled={!vdDate || saving}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
